import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { componentMapping } from '../../../utils/helpers';
import { mediaqueryBreakpoints } from '../../../utils/mediaQuery';
import { isMobile } from '../../../utils/animationHelper';
import {
  getBusinessToggle,
  BUSINESS_TOGGLES,
} from '../../../utils/toggleHelpers';
import { selectCurrentPage } from '../../containers/contentfulHoc/selectors';
import { getPageName } from '../ctaAnalyticsHoc/utils';
import { naturalToKebab } from '../../../utils/stringHelpers';
import { setUtagForLinks } from '../../lib/analytics';
import { DotButton, useDotButton } from './dots';
import { Arrow } from './arrow';
import { SliderWrapper } from './promoCardSliderStyles';

const PromoCardSlider = ({
  columns,
  page,
  groupRowTitle = '',
  restrictContent = true,
  nextText = 'Next',
  prevText = 'Previous',
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      containScroll: false,
      breakpoints: {
        [`(min-width: ${mediaqueryBreakpoints.lg}px)`]: {
          containScroll: true,
          align: 'start',
        },
      },
    },
    [WheelGesturesPlugin()],
  );

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(api => {
    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    setIsMobileView(isMobile());
  }, []);

  const showChevrons = isMobileView
    ? getBusinessToggle(BUSINESS_TOGGLES.PROMO_CARDS_SLIDER_ARROWS_MOBILE)
    : getBusinessToggle(BUSINESS_TOGGLES.PROMO_CARDS_SLIDER_ARROWS_DESKTOP);

  const currentPage = useSelector(selectCurrentPage());
  const pageName = getPageName(currentPage);
  const moduleTitle = naturalToKebab(groupRowTitle);

  const fireAnalytics = ctaText => {
    setUtagForLinks({
      custom_link: `content|${pageName}|promo-cards-slider|${moduleTitle}|${ctaText}`,
    });
  };

  const renderPromoCard = item => {
    const Template = componentMapping[item.identifier];
    if (!Template) return null;
    return (
      <Template
        {...item}
        page={page}
        columnsLength={columns.length}
        groupRowTitle={groupRowTitle}
        restrictContent={restrictContent}
      />
    );
  };

  return (
    <SliderWrapper>
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {columns.map(item => (
            <div className="embla__slide" key={item.id}>
              {renderPromoCard(item)}
            </div>
          ))}
        </div>
      </div>

      {showChevrons && (
        <>
          <Arrow
            className="embla__arrows embla__prev"
            onClick={() => {
              fireAnalytics('arrow-previous');
              scrollPrev();
            }}
            isPrevArrow
            text={prevText}
            isEnabled={!prevBtnDisabled}
          />
          <Arrow
            className="embla__arrows embla__next"
            onClick={() => {
              fireAnalytics('arrow-next');
              scrollNext();
            }}
            isPrevArrow={false}
            text={nextText}
            isEnabled={!nextBtnDisabled}
          />
        </>
      )}

      {scrollSnaps.length > 1 && (
        <div className="embla__dots">
          {scrollSnaps.map((snap, index) => (
            <DotButton
              key={snap}
              onClick={() => {
                fireAnalytics('dot');
                onDotButtonClick(index);
              }}
              isSelected={index === selectedIndex}
            />
          ))}
        </div>
      )}
    </SliderWrapper>
  );
};

PromoCardSlider.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  page: PropTypes.object.isRequired,
  groupRowTitle: PropTypes.string,
  restrictContent: PropTypes.bool,
  nextText: PropTypes.string,
  prevText: PropTypes.string,
};

export default PromoCardSlider;
